/**
 * What a character owns, as state.
 *
 * Three things, and the split between the first two is the whole death rule: `carried` drops where
 * you died, `stash` never leaves the hub, and `equipment` stays on the body. See
 * src/sim/inventory/AGENTS.md.
 *
 * A stack is `defId` plus `count`, and non-stackable defs additionally carry an `instanceId` drawn
 * from a counter in the state itself, plus the roll that made them if they were rolled rather than
 * authored. That counter is why nothing here needs a clock or a random
 * number: two peers stepping the same operations hand out the same ids in the same order.
 *
 * The field names are the save format. Renaming one is a migration, not a rename.
 */

import type { ItemId } from '@/sim/items/itemContract';
import type { RolledWeapon } from '@/sim/items/rolledWeaponContract';
import type { ContainerId } from '@/sim/inventory/inventoryResult';
import type { SlotId } from '@/sim/inventory/slotContract';

/**
 * The inventory schema's version, stored beside the state and checked on load.
 * `inventorySchema.ts` owns what happens when it does not match.
 */
export const INVENTORY_SCHEMA_VERSION = 2;

export interface ItemStack {
  readonly defId: ItemId;
  /** Always at least one, never past the def's `stackLimit`. */
  readonly count: number;
  /** Non-null exactly when the def does not stack, so mods and wear have somewhere to hang. */
  readonly instanceId: number | null;
  /**
   * What the dice made of this one, or null for anything authored rather than rolled.
   *
   * Non-null only on a non-stackable weapon def, and always beside an `instanceId`: two rolls of
   * the same rifle are two different rifles, and a stack is a thing that does not care which one
   * it is holding. `readRolledWeapon` is what enforces that on the way in from a save.
   */
  readonly rolled: RolledWeapon | null;
}

export interface Container {
  readonly capacityKg: number;
  readonly stacks: readonly ItemStack[];
}

export type Equipment = Readonly<Record<SlotId, ItemStack | null>>;

export interface InventoryState {
  readonly version: number;
  readonly carried: Container;
  readonly stash: Container;
  readonly equipment: Equipment;
  /** The next instance id to hand out. Only ever increases, so a freed id is never reused. */
  readonly nextInstanceId: number;
}

/** Nothing equipped. Written out rather than built in a loop, so the union is checked here. */
export const EMPTY_EQUIPMENT: Equipment = {
  primaryWeapon: null,
  sidearm: null,
  head: null,
  torso: null,
  legs: null,
  accessory1: null,
  accessory2: null,
  accessory3: null,
};

export function emptyContainer(capacityKg: number): Container {
  return { capacityKg, stacks: [] };
}

export function createInventory(
  carriedCapacityKg: number,
  stashCapacityKg: number,
): InventoryState {
  return {
    version: INVENTORY_SCHEMA_VERSION,
    carried: emptyContainer(carriedCapacityKg),
    stash: emptyContainer(stashCapacityKg),
    equipment: EMPTY_EQUIPMENT,
    nextInstanceId: 1,
  };
}

/** Reads one container by name, so an operation takes a `ContainerId` and not two code paths. */
export function containerOf(state: InventoryState, id: ContainerId): Container {
  return id === 'carried' ? state.carried : state.stash;
}

/** The other half: one container replaced, everything else identical. */
export function withContainer(
  state: InventoryState,
  id: ContainerId,
  container: Container,
): InventoryState {
  return id === 'carried' ? { ...state, carried: container } : { ...state, stash: container };
}
